import {
  Attendance,
  AttendanceDay,
  AttendanceMonth,
  AttendanceVoce,
  Giustificativo,
  LanguageLocaleEnum,
} from "@/api/openapi";
import { translate } from "@/config/translator";
import {
  TempAttendance,
  TempAttendanceDay,
  TempAttendanceMonth,
  TempGiustificativo,
} from "@/config/types";
import { Dispatch, SetStateAction } from "react";
import { toast } from "react-toastify";

export const POSITIVE_GIUSTIFICATIVI = ["SB", "SF"];

// value is expressed in a comma floating point fashion
// i.e. 6,2 ; 8,4 ; etc.
export function updateAttandenceAfterRegularHoursChange(
  attendanceMonth: AttendanceMonth | undefined,
  employeeAttendance: Attendance | TempAttendance,
  value: string,
  currentEditingValue: string | null,
  focusedDay: number | null,
  lang: LanguageLocaleEnum,
  resetState: () => void,
): AttendanceMonth {
  if (focusedDay === null) {
    toast(translate("somethingWentWrong", lang));
    resetState();
    throw new Error(
      "onBlur of regular hours input was triggered, but focusedDay is null.",
    );
  }
  if (attendanceMonth === undefined) {
    toast(translate("somethingWentWrong", lang));
    resetState();
    throw new Error(
      "onBlur of regular hours input was triggered, but attendanceMonth is undefined.",
    );
  }
  let valueNumber = parseHours(value);

  // make sure you actually changed something before saving
  if (
    currentEditingValue !== null &&
    !Number.isNaN(valueNumber) &&
    valueNumber !== parseHours(currentEditingValue)
  ) {
    // don't allow negative numbers
    valueNumber = Math.max(valueNumber, 0);
    const relevantAttendance = employeeAttendance.days[focusedDay];
    const giustificativiSum = relevantAttendance.giustificativi.reduce(
      // NOTE: claude suggested this line, I hope he's right
      (sum, g) => POSITIVE_GIUSTIFICATIVI.includes(g.type) ? sum : sum + Number(g.hours),
      0,
    );
    const theoreticalHours = Number(relevantAttendance.theoreticalHours);
    // don't allow less than theoretical hours
    let allowedHours: number;
    if (valueNumber >= theoreticalHours) {
      allowedHours = valueNumber;
    } else {
      allowedHours = theoreticalHours;
      toast(translate("actualHoursCannotBeLessThanTheoreticalHours", lang));
    }
    const newAttendance: AttendanceMonth = {
      ...attendanceMonth,
      attendances: attendanceMonth.attendances.map((a) =>
        a.employee.filiale === employeeAttendance.employee.filiale &&
        a.employee.matricola === employeeAttendance.employee.matricola
          ? {
              ...a,
              days: a.days.map((d, i) =>
                i === focusedDay
                  ? {
                      ...d,
                      actualHours: allowedHours + giustificativiSum,
                    }
                  : d,
              ),
            }
          : a,
      ),
    };
    resetState();
    return newAttendance;
  } else {
    resetState();
    if (currentEditingValue === null) {
      toast(translate("somethingWentWrong", lang));
      throw new Error(
        "onBlur was triggered for a regular hours input, but currentEditingValue was undefined.",
      );
    }
    return attendanceMonth;
  }
}

export function addVoceToAttendance(
  attendanceMonth: AttendanceMonth,
  employeeMatricola: number,
  employeeFiliale: number,
  voce: AttendanceVoce,
): AttendanceMonth {
  return {
    ...attendanceMonth,
    attendances: attendanceMonth.attendances.map((a) =>
      a.employee.matricola === employeeMatricola &&
      a.employee.filiale === employeeFiliale
        ? { ...a, voci: [voce, ...a.voci] }
        : a,
    ),
  };
}

export function removeVoceFromAttendance(
  attendanceMonth: AttendanceMonth,
  employeeMatricola: number,
  employeeFiliale: number,
  voceIndex: number,
): AttendanceMonth {
  return {
    ...attendanceMonth,
    attendances: attendanceMonth.attendances.map((a) =>
      a.employee.matricola === employeeMatricola &&
      a.employee.filiale === employeeFiliale
        ? { ...a, voci: a.voci.toSpliced(voceIndex, 1) }
        : a,
    ),
  };
}

// this shit is a fucking mess
export function onChangeTempAttendanceMonth(
  setTempAttendanceMonth: Dispatch<
    SetStateAction<TempAttendanceMonth | undefined>
  >,
  employeeMatricola: number,
  employeeFiliale: number,
  day: number,
  hours: string,
  // is null when the user modified the actual work hours of the day directly
  // (i.e. not a giustificativo)
  giustificativo: string | null,
) {
  let parsedHours = parseHours(hours);
  if (Number.isNaN(parsedHours) && hours !== "") return;
  if (hours === "") {
    parsedHours = 0;
  }
  if (!Number.isNaN(parsedHours) && parsedHours > 24) {
    hours = "24,00";
    parsedHours = 24;
  }

  setTempAttendanceMonth((prev) =>
    prev
      ? {
          ...prev,
          attendances: prev.attendances.map((attendance) =>
            attendance.employee.matricola === employeeMatricola &&
            attendance.employee.filiale === employeeFiliale
              ? {
                  ...attendance,
                  days: attendance.days.map((attendanceDay, index) =>
                    index === day
                      ? {
                          ...attendanceDay,
                          actualHours:
                            giustificativo === null
                              ? hours
                              : attendanceDay.actualHours,
                          giustificativi:
                            giustificativo === null
                              ? attendanceDay.giustificativi
                              : attendanceDay.giustificativi.map((g) => {
                                  if (g.type === giustificativo) {
                                    const actualHours = parseHours(
                                      attendanceDay.actualHours,
                                    );

                                    const otherGiustificativi =
                                      attendanceDay.giustificativi.reduce(
                                        (sum, innerG) =>
                                          innerG.type === g.type
                                            ? sum
                                            : sum + parseHours(innerG.hours),
                                        0,
                                      );

                                    const availableHours =
                                      actualHours - otherGiustificativi;

                                    const allowedHours = Math.max(
                                      availableHours,
                                      0,
                                    );
                                    return {
                                      ...g,
                                      hours:
                                        hours === ""
                                          ? ""
                                          : parsedHours < allowedHours
                                            ? hours.replace(",", ".")
                                            : String(allowedHours),
                                    };
                                  } else {
                                    return g;
                                  }
                                }),
                        }
                      : attendanceDay,
                  ),
                }
              : attendance,
          ),
        }
      : prev,
  );
}

/**
 * bro i know this sucks, but i swear i was not able to think of a better solution
 * which would work quickly, so this is what we're stuck with.
 */
export function attendanceToTempAttendance(
  attendanceMonth: AttendanceMonth,
): TempAttendanceMonth {
  return {
    ...attendanceMonth,
    attendances: attendanceMonth.attendances.map(
      (month) =>
        ({
          ...month,
          days: month.days.map(
            (tempDay) =>
              ({
                ...tempDay,
                actualHours: tempDay.actualHours.toString(),
                giustificativi: tempDay.giustificativi.map(
                  (tempGiustificativo) =>
                    ({
                      ...tempGiustificativo,
                      hours: tempGiustificativo.hours.toString(),
                    }) satisfies TempGiustificativo,
                ),
              }) satisfies TempAttendanceDay,
          ),
        }) satisfies TempAttendance,
    ),
  } satisfies TempAttendanceMonth;
}

export function tempAttendanceToAttendance(
  tempAttendanceMonth: TempAttendanceMonth,
): AttendanceMonth {
  return {
    ...tempAttendanceMonth,
    attendances: tempAttendanceMonth.attendances.map(
      (tempMonth) =>
        ({
          ...tempMonth,
          days: tempMonth.days.map(
            (tempDay) =>
              ({
                ...tempDay,
                actualHours: Number.parseFloat(tempDay.actualHours),
                giustificativi: tempDay.giustificativi.map(
                  (tempGiustificativo) =>
                    ({
                      ...tempGiustificativo,
                      hours: Number.parseFloat(tempGiustificativo.hours),
                    }) satisfies Giustificativo,
                ),
              }) satisfies AttendanceDay,
          ),
        }) satisfies Attendance,
    ),
  } satisfies AttendanceMonth;
}

export function addGiustificativoToEmployee(
  attendanceMonth: AttendanceMonth,
  giustificativo: Giustificativo,
  employeeMatricola: number,
  employeeFiliale: number,
): AttendanceMonth {
  return {
    ...attendanceMonth,
    attendances: attendanceMonth.attendances.map((attendance) => ({
      ...attendance,
      days:
        attendance.employee.matricola === employeeMatricola &&
        attendance.employee.filiale === employeeFiliale
          ? attendance.days.map((day) => ({
              ...day,
              giustificativi: [...day.giustificativi, giustificativo],
            }))
          : attendance.days,
    })),
  };
}

export function removeGiustificativoFromEmployee(
  attendanceMonth: AttendanceMonth,
  giustificativoType: string,
  employeeMatricola: number,
  employeeFiliale: number,
): AttendanceMonth {
  return {
    ...attendanceMonth,
    attendances: attendanceMonth.attendances.map((attendance) => ({
      ...attendance,
      days:
        attendance.employee.matricola === employeeMatricola &&
        attendance.employee.filiale === employeeFiliale
          ? attendance.days.map((day) => ({
              ...day,
              giustificativi: day.giustificativi.filter(
                (g) => g.type !== giustificativoType,
              ),
            }))
          : attendance.days,
    })),
  };
}

export function parseHours(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === "") {
    return NaN;
  }

  const parsed = Number(String(value).trim().replace(",", "."));
  return Number.isFinite(parsed) ? parsed : NaN;
}

export function formatHours(value: number): string {
  return value
    .toFixed(2)
    .replace(/\.?0+$/, "")
    .replace(".", ",");
}
